/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Clock, Copy, Download, History, Loader2, RefreshCw, ShieldCheck, Stamp } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import api, { describeError } from '@/lib/api';
import { useAsyncAction } from '@/hooks/useAsyncAction';
import { fmt, short, copy, downloadBlob, bytes } from './helpers';
import { TsaConfigCard } from './TsaConfigCard';

/* ------------------------------------------------------------------ Timestamp button */
export const TimestampButton = ({ sha256, filename, kind = 'document', existing, onDone, size = 'sm' }) => {
  const { run, busy } = useAsyncAction();
  const stamp = () => run(async () => {
    const r = await api.post('/legal/timestamp', { sha256, filename, kind });
    onDone?.(r.data);
    return r.data;
  }, { success: (d) => `Marca temporale ottenuta · ${fmt(d.gen_time)} · ${d.signer_cn || d.tsa_url}` });

  const downloadTsr = async (ts) => {
    try {
      const r = await api.get(`/legal/timestamps/${ts.id}/tsr`, { responseType: 'blob' });
      downloadBlob(r.data, `${filename || ts.sha256.slice(0, 12)}.tsr`);
    } catch (e) { toast.error(describeError(e)); }
  };

  if (existing) {
    return (
      <div className="inline-flex items-center gap-1" data-testid={`timestamp-done-${sha256.slice(0, 8)}`}>
        <Badge className="rounded-full bg-primary/10 text-primary hover:bg-primary/10" title={`${existing.signer_cn || existing.tsa_url} · serial ${existing.serial_number}`}>
          <Stamp className="h-3 w-3 mr-1" /> Marcato {fmt(existing.gen_time).slice(0, 15)}
        </Badge>
        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => downloadTsr(existing)} title="Scarica ricevuta .tsr" data-testid={`timestamp-tsr-${sha256.slice(0, 8)}`}><Download className="h-3.5 w-3.5" /></Button>
      </div>
    );
  }
  return (
    <Button size={size} variant="outline" className="rounded-xl" onClick={stamp} disabled={busy || !sha256} data-testid={`timestamp-btn-${sha256?.slice(0, 8)}`}>
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Stamp className="h-3.5 w-3.5" />} Marca temporale
    </Button>
  );
};

/* ------------------------------------------------------------------ PDF sigillato */
export const SealedDownloadButton = ({ sha256, filename, size = 'sm' }) => {
  const { run, busy } = useAsyncAction();
  const download = () => run(async () => {
    const r = await api.get(`/legal/exports/${sha256}/sealed.pdf`, { responseType: 'blob' });
    const base = (filename || sha256.slice(0, 12)).replace(/\.pdf$/i, '');
    downloadBlob(r.data, `${base}-sigillato.pdf`);
    return r.headers['x-document-sha256'] || '';
  }, { success: (d) => `PDF sigillato scaricato · SHA-256 ${short(d)}` });
  return (
    <Button size={size} variant="outline" className="rounded-xl border-primary/40 text-primary hover:bg-primary/5" onClick={download} disabled={busy} title="Originale + pagina di sigillo con i dati della marca + allegati (originale e .tsr)" data-testid={`sealed-btn-${sha256.slice(0, 8)}`}>
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />} PDF sigillato
    </Button>
  );
};

/* ------------------------------------------------------------------ Storico export */
export const ExportHistory = ({ refreshKey }) => {
  const [data, setData] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [ex, c] = await Promise.all([api.get('/legal/exports'), api.get('/legal/timestamp/config')]);
      setData(ex.data); setCfg(c.data);
    } catch (e) { setError(describeError(e, 'Impossibile caricare lo storico')); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const onStamped = (ts) => setData((d) => d ? { ...d, items: d.items.map((it) => (it.sha256 === ts.sha256 && !it.timestamp ? { ...it, timestamp: ts, sealable: it.stored } : it)) } : d);

  return (
    <div className="not-prose space-y-3" data-testid="export-history">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-display text-lg font-semibold flex items-center gap-2"><History className="h-4 w-4 text-primary" /> Storico export e marche temporali</h3>
        <Button variant="ghost" size="sm" className="h-8" onClick={load} disabled={loading} data-testid="export-history-reload"><RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Aggiorna</Button>
      </div>
      <TsaConfigCard cfg={cfg} onSaved={setCfg} />
      <Card className="rounded-2xl overflow-hidden">
        {error ? (
          <CardContent className="p-5 text-sm text-destructive flex items-center justify-between gap-3"><span>{error}</span><Button variant="outline" size="sm" onClick={load}>Riprova</Button></CardContent>
        ) : loading ? (
          <CardContent className="p-4 space-y-2">{[0, 1].map((i) => <Skeleton key={i} className="h-9 w-full" />)}</CardContent>
        ) : !data?.items?.length ? (
          <CardContent className="p-6 text-center text-sm text-muted-foreground" data-testid="export-history-empty">Nessun export generato finora. Usa "Scarica PDF prova legale" qui sopra.</CardContent>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="export-history-table">
              <TableHeader><TableRow><TableHead className="w-24">Data/ora</TableHead><TableHead>Export</TableHead><TableHead className="w-28">SHA-256</TableHead><TableHead className="w-44">Marca temporale</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.items.map((it) => (
                  <TableRow key={it.audit_id} data-testid={`export-row-${it.seq}`}>
                    <TableCell className="text-xs align-top">{fmt(it.at).replace(', ', '\n').split('\n').map((l, i) => <div key={i === 0 ? 'date' : 'time'} className={i ? 'text-muted-foreground' : ''}>{l}</div>)}</TableCell>
                    <TableCell className="text-xs align-top">
                      <div className="flex items-center gap-1.5"><Badge variant="outline" className="rounded-full">{it.format}</Badge><span className="font-mono break-all">{it.filename}</span></div>
                      <div className="text-muted-foreground mt-0.5">{it.count} accettazioni{it.bytes ? ` · ${bytes(it.bytes)}` : ''}{it.actor_email ? ` · ${it.actor_email}` : ''}</div>
                    </TableCell>
                    <TableCell className="text-xs align-top"><button type="button" className="font-mono hover:text-primary inline-flex items-center gap-1" onClick={() => copy(it.sha256, 'SHA-256 copiato')} title={it.sha256}>{short(it.sha256)} <Copy className="h-3 w-3" /></button></TableCell>
                    <TableCell className="text-xs align-top">
                      <div className="flex flex-wrap items-center gap-1.5">
                        {it.sha256 ? <TimestampButton sha256={it.sha256} filename={it.filename} kind={`export_${it.format.toLowerCase()}`} existing={it.timestamp} onDone={onStamped} /> : '—'}
                        {it.timestamp && it.format === 'PDF' && it.stored && <SealedDownloadButton sha256={it.sha256} filename={it.filename} />}
                        {it.timestamp && it.format === 'PDF' && !it.stored && <span className="text-[11px] text-muted-foreground" title="I byte del PDF non sono conservati per gli export precedenti all'attivazione del sigillo">sigillo non disponibile</span>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
      <p className="text-[11px] text-muted-foreground flex items-start gap-1.5"><Clock className="h-3.5 w-3.5 mt-0.5 shrink-0" /> Le TSA gratuite rilasciano marche RFC 3161 (prova tecnica di esistenza a una data). Per la presunzione legale piena in Italia scegli un QTSP eIDAS nel menu "Configura TSA". Conserva sempre insieme: file, SHA-256 e ricevuta .tsr.</p>
    </div>
  );
};
